import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { Protocol } from 'pmtiles'
import { layers, LIGHT } from '@protomaps/basemaps'

const PMTILES_URL = process.env.VUE_APP_MAPS_PMTILES_URL

// Label fonts and POI icons for the Protomaps basemap, hosted by Protomaps.
const PROTOMAPS_ASSETS_URL = 'https://protomaps.github.io/basemaps-assets'

// Google-Maps-like grayscale palette mirroring the pwa-desk basemap flavor.
const GMAPS_FLAVOR = {
    ...LIGHT,
    background: '#f5f5f5',
    earth: '#f5f5f5',
    glacier: '#f5f5f5',
    sand: '#f5f5f5',
    beach: '#f5f5f5',
    buildings: '#e8e8e8',
    water: '#c2cad1',
    pier: '#eeeeee',
    park_a: '#e5e5e5',
    park_b: '#e5e5e5',
    wood_a: '#e5e5e5',
    wood_b: '#e5e5e5',
    scrub_a: '#e5e5e5',
    scrub_b: '#e5e5e5',
    zoo: '#e5e5e5',
    hospital: '#eeeeee',
    school: '#eeeeee',
    industrial: '#eeeeee',
    pedestrian: '#eeeeee',
    military: '#eeeeee',
    aerodrome: '#eeeeee',
    highway: '#dadada',
    bridges_highway: '#dadada',
    tunnel_highway: '#dadada',
    railway: '#e5e5e5',
    boundaries: '#bdbdbd',
    ocean_label: '#c2cad1',
    city_label: '#616161',
    country_label: '#616161',
    state_label: '#bdbdbd',
    roads_label_major: '#757575',
    roads_label_minor: '#9e9e9e',
    landcover: {
        grassland: '#e8e8e8',
        barren: '#f0f0f0',
        urban_area: '#f0f0f0',
        farmland: '#ececec',
        glacier: '#f5f5f5',
        scrub: '#e8e8e8',
        forest: '#e5e5e5',
    },
}

// addProtocol registers globally on MapLibre, so guard against doing it twice.
let isProtocolRegistered = false

function registerPmtilesProtocol() {
    if (isProtocolRegistered) return

    maplibregl.addProtocol('pmtiles', new Protocol().tile)
    isProtocolRegistered = true
}

function buildStyle() {
    return {
        version: 8,
        glyphs: `${PROTOMAPS_ASSETS_URL}/fonts/{fontstack}/{range}.pbf`,
        sprite: `${PROTOMAPS_ASSETS_URL}/sprites/v4/light`,
        sources: {
            protomaps: {
                type: 'vector',
                url: `pmtiles://${PMTILES_URL}`,
                attribution: '© OpenStreetMap',
            },
        },
        layers: layers('protomaps', GMAPS_FLAVOR, { lang: 'fr' }),
    }
}

function buildMarkerElement(iconUrl, size) {
    const element = document.createElement('img')
    element.src = iconUrl
    element.width = size
    element.height = size
    element.style.display = 'block'

    return element
}

// --- Position track (multi-point + polyline) -------------------------------

const TRACK_POINTS_SOURCE = 'track-points'
const TRACK_LINE_SOURCE = 'track-line'

// Track colors, matching the widget legend.
const TRACK_COLOR_START = '#EA4335'
const TRACK_COLOR_END = '#65B265'
const TRACK_COLOR_MID = '#1DB9F3'

const TRACK_ICON_BY_TYPE = {
    start: 'circle-start',
    end: 'circle-end',
    mid: 'circle-mid',
}
const TRACK_COLOR_BY_ICON = {
    'circle-start': TRACK_COLOR_START,
    'circle-end': TRACK_COLOR_END,
    'circle-mid': TRACK_COLOR_MID,
}

function svgToDataUrl(svg) {
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg).replace(/'/g, '%27').replace(/"/g, '%22')}`
}

function getCircleMarkerSvgUrl(circleColor = '#FFFFFF', strokeColor = circleColor) {
    return svgToDataUrl(`
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" width="20" height="20">
            <circle cx="10" cy="10" r="9" fill="${circleColor}" stroke="${strokeColor}" stroke-width="1"/>
        </svg>
    `)
}

function loadImage(url) {
    return new Promise((resolve, reject) => {
        const image = new Image()
        image.crossOrigin = 'anonymous'
        image.onload = () => resolve(image)
        image.onerror = reject
        image.src = url
    })
}

function trackPointsToFeatureCollection(points) {
    return {
        type: 'FeatureCollection',
        features: points.map((point) => ({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [point.longitude, point.latitude] },
            properties: {
                icon: TRACK_ICON_BY_TYPE[point.type],
                iconSize: point.type === 'mid' ? 0.4 : 0.6,
                date: point.date_releve,
            },
        })),
    }
}

function trackLineFeature(points) {
    return {
        type: 'Feature',
        geometry: { type: 'LineString', coordinates: points.map((point) => [point.longitude, point.latitude]) },
        properties: {},
    }
}

// MapLibre helper for the asset localisation and position history widgets.
// The map, marker and popup instances are kept as non-reactive component
// properties (never declared in data()) to avoid Vue observing MapLibre objects.
export default {
    methods: {
        initMapLibre(container, { center, zoom }) {
            this.destroyMapLibre()
            registerPmtilesProtocol()

            this._mapLibreMap = new maplibregl.Map({
                container,
                style: buildStyle(),
                center: [center.lng, center.lat],
                zoom,
                attributionControl: false,
            })
            this._mapLibreMap.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'bottom-right')

            this._mapLibreLoaded = false
            this._mapLibreMap.on('load', () => {
                this._mapLibreLoaded = true
            })

            return this._mapLibreMap
        },
        // Sources/layers can only be added once the style has loaded.
        whenMapReady(task) {
            if (!this._mapLibreMap) return

            if (this._mapLibreLoaded) task()
            else this._mapLibreMap.once('load', task)
        },
        setSingleMarker({ lat, lng, iconUrl, size, anchor = 'bottom' }) {
            if (!this._mapLibreMap) return

            const position = [lng, lat]

            if (this._mapLibreMarker) {
                this._mapLibreMarker.setLngLat(position)
                return
            }

            const options = iconUrl ? { element: buildMarkerElement(iconUrl, size), anchor } : { anchor }
            this._mapLibreMarker = new maplibregl.Marker(options).setLngLat(position).addTo(this._mapLibreMap)
        },
        panMapTo({ lat, lng }) {
            this._mapLibreMap?.panTo([lng, lat])
        },
        // Renders (or updates) a position track: a polyline plus colored circle
        // markers (start/end/intermediate) generated from getCircleMarkerSvgUrl.
        renderTrack({ points }) {
            if (!this._mapLibreMap || !points.length) return

            this.whenMapReady(async () => {
                await this.registerTrackImages()

                const pointsData = trackPointsToFeatureCollection(points)
                const lineData = trackLineFeature(points)

                if (this._mapLibreMap.getSource(TRACK_POINTS_SOURCE)) {
                    this._mapLibreMap.getSource(TRACK_POINTS_SOURCE).setData(pointsData)
                    this._mapLibreMap.getSource(TRACK_LINE_SOURCE).setData(lineData)
                    return
                }

                this._mapLibreMap.addSource(TRACK_LINE_SOURCE, { type: 'geojson', data: lineData })
                this._mapLibreMap.addLayer({
                    id: TRACK_LINE_SOURCE,
                    type: 'line',
                    source: TRACK_LINE_SOURCE,
                    layout: { 'line-cap': 'round', 'line-join': 'round' },
                    paint: { 'line-color': TRACK_COLOR_MID, 'line-opacity': 0.6, 'line-width': 6 },
                })

                this._mapLibreMap.addSource(TRACK_POINTS_SOURCE, { type: 'geojson', data: pointsData })
                this._mapLibreMap.addLayer({
                    id: TRACK_POINTS_SOURCE,
                    type: 'symbol',
                    source: TRACK_POINTS_SOURCE,
                    layout: {
                        'icon-image': ['get', 'icon'],
                        'icon-size': ['get', 'iconSize'],
                        'icon-allow-overlap': true,
                    },
                })

                this.addTrackInteractions()
            })
        },
        async registerTrackImages() {
            await Promise.all(
                Object.entries(TRACK_COLOR_BY_ICON).map(async ([id, color]) => {
                    if (this._mapLibreMap.hasImage(id)) return

                    const image = await loadImage(getCircleMarkerSvgUrl(color, color))
                    if (!this._mapLibreMap.hasImage(id)) this._mapLibreMap.addImage(id, image)
                })
            )
        },
        addTrackInteractions() {
            this._mapLibreMap.on('click', TRACK_POINTS_SOURCE, (event) => {
                const feature = event.features?.[0]
                if (!feature) return

                this._mapLibrePopup?.remove()
                this._mapLibrePopup = new maplibregl.Popup()
                    .setLngLat(feature.geometry.coordinates.slice())
                    .setHTML(`<div class="text-skin-default">${feature.properties.date}</div>`)
                    .addTo(this._mapLibreMap)
            })

            this._mapLibreMap.on('mouseenter', TRACK_POINTS_SOURCE, () => {
                this._mapLibreMap.getCanvas().style.cursor = 'pointer'
            })
            this._mapLibreMap.on('mouseleave', TRACK_POINTS_SOURCE, () => {
                this._mapLibreMap.getCanvas().style.cursor = ''
            })
        },
        fitTrackBounds({ points }) {
            if (!this._mapLibreMap || !points.length) return

            this.whenMapReady(() => {
                const bounds = new maplibregl.LngLatBounds()
                points.forEach((point) => bounds.extend([point.longitude, point.latitude]))
                this._mapLibreMap.fitBounds(bounds, { padding: 40, maxZoom: 16 })
            })
        },
        clearTrack() {
            this._mapLibrePopup?.remove()
            this._mapLibrePopup = null
            this._mapLibreMap?.getSource(TRACK_POINTS_SOURCE)?.setData(trackPointsToFeatureCollection([]))
            this._mapLibreMap?.getSource(TRACK_LINE_SOURCE)?.setData(trackLineFeature([]))
        },
        resizeMapLibre() {
            this._mapLibreMap?.resize()
        },
        destroyMapLibre() {
            this._mapLibrePopup?.remove()
            this._mapLibrePopup = null
            this._mapLibreMarker?.remove()
            this._mapLibreMarker = null
            this._mapLibreMap?.remove()
            this._mapLibreMap = null
            this._mapLibreLoaded = false
        },
    },
    beforeDestroy() {
        this.destroyMapLibre()
    },
}
